import { Box, styled } from '@mui/material';
import { FileEarmarkText } from '@styled-icons/bootstrap';
import DocumentIntroTitle from 'FeatureModules/Document/Components/DocumentIntroTitle';
import {
  ActionButton,
  ErrorMessage,
  FileNameText,
  ProgressContainer,
  ProgressNumber,
  StyledProgress,
} from 'Components/FileUpload/styles';
import useDialog from 'hooks/useDialog';
import useUploadItem from 'FeatureModules/Document/hooks/useUploadItem';
import useUploadState from 'FeatureModules/Document/hooks/useUploadState';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React, { FC, useMemo } from 'react';
import { Tbooleanish } from 'types/booleanish.types';
import { TDatasetProcessingErrorType } from 'FeatureModules/DataImport/types/dataset.types';
import FlexBox from 'Components/FlexBox';

interface IUploadItemProps {
  id: string;
  processingError?: { type?: TDatasetProcessingErrorType; message: string };
  allowDocumentEditing?: boolean;
  onFileDelete?: (documentRecordId: string) => void;
}

export const UploadItemContainer = styled('div')<{ error?: Tbooleanish }>(({ theme, error }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5),
  color: theme.palette.text.secondary,
  border: '1px solid',
  borderColor: error ? theme.palette.warning.main : theme.palette.grey[200],
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  background: '#fff',
}));

const UploadItem: FC<IUploadItemProps> = ({
  id,
  processingError,
  allowDocumentEditing,
  onFileDelete,
}) => {
  // @ts-ignore
  const { data, progress, error: uploadError, success, recordId, itemType } = useUploadItem(id);
  const { removeFile, createdRecords, updateRecordsFromCache } = useUploadState();
  const { openDialog } = useDialog();

  const uploading = !uploadError && !success;

  const documentRecord = useMemo(() => {
    if (itemType !== 'document') return null;
    return createdRecords.find(record => record.id === recordId);
  }, [createdRecords, itemType, recordId]);

  const handleDelete = () => {
    onFileDelete?.(recordId);
    removeFile(id);
  };

  return (
    <UploadItemContainer>
      {!uploading && documentRecord && !uploadError && !processingError ? (
        <DocumentIntroTitle document={documentRecord} />
      ) : (
        <Box display="flex" alignItems="center" width="90%">
          <FileEarmarkText size={28} />
          <Box display="flex" flexGrow={1} flexDirection="column" width="70%" gap={0.5}>
            <Box>
              <FileNameText>{data.name}</FileNameText>
            </Box>
            {(uploadError || processingError) && (
              <ErrorMessage error>{uploadError || processingError?.message}</ErrorMessage>
            )}
          </Box>
        </Box>
      )}
      {uploading && (
        <ProgressContainer>
          <ProgressNumber>{progress}%</ProgressNumber>
          <StyledProgress color="secondary" variant="determinate" value={progress} />
        </ProgressContainer>
      )}
      <FlexBox gap={1}>
        {allowDocumentEditing && documentRecord && (
          <ActionButton
            onClick={() => {
              if (documentRecord) {
                openDialog({
                  type: 'EDIT_DOCUMENT',
                  props: {
                    document: documentRecord,
                    onDelete: handleDelete,
                    onUpdate: updateRecordsFromCache,
                  },
                });
              }
            }}
          >
            <Icon name="edit" />
          </ActionButton>
        )}
        <ActionButton onClick={handleDelete}>
          <Icon name="delete" />
        </ActionButton>
      </FlexBox>
    </UploadItemContainer>
  );
};

export default UploadItem;
